import React from 'react';

export const LANGS = ['ko', 'en'] as const;
export type Lang = (typeof LANGS)[number];
export const DEFAULT_LANG: Lang = 'ko';

export type Alternate = { lang: Lang; path: string };

export const HTML_LANG: Record<Lang, string> = { ko: 'ko', en: 'en' };
export const OG_LOCALE: Record<Lang, string> = { ko: 'ko_KR', en: 'en_US' };

export function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGS as readonly string[]).includes(value);
}

export function listPath(lang: Lang) {
  return lang === DEFAULT_LANG ? '/' : `/${lang}/`;
}

export function resolveLang(pageContext: unknown, pathname: string): Lang {
  const lang = (pageContext as { lang?: unknown } | undefined)?.lang;
  if (isLang(lang)) return lang;
  const prefix = pathname.split('/')[1];
  return isLang(prefix) ? prefix : DEFAULT_LANG;
}

const messages = {
  ko: {
    'site.title': '데브시스터즈 기술 블로그: DEVTECH',
    'site.description':
      '데브시스터즈의 서비스를 만드는 개발자들이 경험한 실제 개발 사례를 중심으로 기술과 노하우를 생생하게 전달합니다.',
    'nav.blog': '기술 블로그',
    'nav.careers': '채용',
    'nav.switchLang': 'English',
    'nav.switchLangShort': 'EN',
    'kv.line1': '데브시스터즈의 서비스를 만드는 개발자들의 ',
    'kv.line2': '실제 개발 사례를 중심으로',
    'kv.line3': '기술과 노하우를 생생하게 전달합니다.',
    'search.placeholder': '제목 또는 작성자 검색',
    'search.label': '포스트 검색',
    'search.clear': '검색어 지우기',
    'search.empty': '"{q}"에 대한 검색 결과가 없습니다.',
    'category.title': '카테고리',
    'category.all': '전체',
    'category.empty': '해당 카테고리의 포스트가 없습니다.',
    'list.moreInOtherLang': '',
    'post.share': '공유하기',
    'post.copyLink': '링크 복사',
    'post.copied': '주소가 복사되었습니다.',
    'post.prev': '이전 글',
    'post.next': '다음 글',
    'post.backToList': '목록으로',
    'post.readIn': 'Read in English',
    'post.suggest': 'This article is also available in English.',
    'post.suggestDismiss': 'Dismiss',
    'post.mtNotice': '',
    'post.mtOriginal': '',
    'hiring.title': '데브시스터즈는 최고의 인재를 찾고 있습니다.',
    'hiring.lookingForPrefix': '데브시스터즈에서는 능력있는 ',
    'hiring.lookingForSuffix': '를 찾고 있습니다.',
    'hiring.and': '와 ',
    'hiring.detail': '자세한 내용은 채용 사이트를 확인해 주세요!',
    'hiring.cta': '채용 바로가기',
  },
  en: {
    'site.title': 'Devsisters Tech Blog: DEVTECH',
    'site.description':
      'Real-world engineering stories from the developers behind Devsisters games and services, sharing the technology and know-how we use in production.',
    'nav.blog': 'Tech Blog',
    'nav.careers': 'Careers',
    'nav.switchLang': '한국어',
    'nav.switchLangShort': 'KO',
    'kv.line1': 'Real-world engineering stories ',
    'kv.line2': 'from the developers behind Devsisters,',
    'kv.line3': 'sharing what we learned in production.',
    'search.placeholder': 'Search by title or author',
    'search.label': 'Search posts',
    'search.clear': 'Clear search',
    'search.empty': 'No results for "{q}".',
    'category.title': 'Categories',
    'category.all': 'All',
    'category.empty': 'No posts in this category yet.',
    'list.moreInOtherLang': 'More articles are available in Korean →',
    'post.share': 'Share',
    'post.copyLink': 'Copy link',
    'post.copied': 'Link copied to clipboard.',
    'post.prev': 'Previous',
    'post.next': 'Next',
    'post.backToList': 'All posts',
    'post.readIn': '한국어로 읽기',
    'post.suggest': '',
    'post.suggestDismiss': '',
    'post.mtNotice':
      'This article was translated from the Korean original using AI. Some nuances may differ from the original.',
    'post.mtOriginal': 'Read the original (Korean)',
    'hiring.title': 'Devsisters is hiring.',
    'hiring.lookingForPrefix': 'We are looking for talented ',
    'hiring.lookingForSuffix': ' to join us.',
    'hiring.and': ' and ',
    'hiring.detail': 'Visit our careers site for details.',
    'hiring.cta': 'View openings',
  },
} satisfies Record<Lang, Record<string, string>>;

export type MessageKey = keyof (typeof messages)['ko'];

export function translate(lang: Lang, key: MessageKey, params?: Record<string, string>) {
  const template: string = messages[lang][key];
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, name) => params[name] ?? '');
}

export const LangContext = React.createContext<Lang>(DEFAULT_LANG);

export function useLang() {
  return React.useContext(LangContext);
}

export function useT() {
  const lang = useLang();
  return React.useCallback((key: MessageKey, params?: Record<string, string>) => translate(lang, key, params), [lang]);
}

export const LIST_ALTERNATES: Alternate[] = LANGS.map((lang) => ({ lang, path: listPath(lang) }));

export function languageSwitchTarget(lang: Lang, alternates: Alternate[] = []): Alternate {
  const target = LANGS.find((it) => it !== lang) || DEFAULT_LANG;
  return alternates.find((it) => it.lang === target) || { lang: target, path: listPath(target) };
}
